import React, { useCallback } from 'react';

import IconButton from '@atlaskit/button/icon/button';
import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { token } from '@atlaskit/tokens';

import type {
	ConnectionRowAction,
	ConnectionRowInteractionHandler,
} from './utils/connectionRowActionTypes.ts';
import type { EnhancedSmartLink } from './utils/smartLinkTypes.ts';

const styles = cssMap({
	cluster: {
		display: 'inline-flex',
		alignItems: 'center',
		// Anchor the cluster to the top of the row so the icons sit level with the
		// title rather than the vertical centre of a multi-line row.
		alignSelf: 'flex-start',
		gap: token('space.050'),
		flexShrink: 0,
	},
});

type ConnectionsByLineRowActionButtonProps = {
	action: ConnectionRowAction;
	item: EnhancedSmartLink;
	onAction?: ConnectionRowInteractionHandler;
};

const ConnectionsByLineRowActionButton = ({
	action,
	item,
	onAction,
}: ConnectionsByLineRowActionButtonProps) => {
	const handleClick = useCallback(
		(event: React.MouseEvent<HTMLButtonElement>) => {
			// The row itself is clickable — it scrolls to the rendered link or opens
			// it in a new tab — so an action click must not bubble into that handler.
			event.stopPropagation();
			action.onClick(item);
			onAction?.(item, action.id);
		},
		[action, item, onAction],
	);

	return (
		<IconButton
			appearance="subtle"
			spacing="compact"
			icon={action.icon}
			label={action.label}
			isTooltipDisabled={false}
			onClick={handleClick}
			testId={`connections-byline-row-action-${action.id}`}
		/>
	);
};

export type ConnectionsByLineRowActionsProps = {
	/** Actions to render, in order. */
	actions: ConnectionRowAction[];
	/** The row's link, passed to each action's `onClick`. */
	item: EnhancedSmartLink;
	/** Called after any action's own `onClick`, with that action's `id`. */
	onAction?: ConnectionRowInteractionHandler;
};

/**
 * The trailing icon-button cluster on a connections row. Every row action —
 * whether supplied by the owning section or derived inside the row — renders
 * through here so they share one appearance and one click contract.
 */
export const ConnectionsByLineRowActions = ({
	actions,
	item,
	onAction,
}: ConnectionsByLineRowActionsProps): React.JSX.Element => (
	<Box xcss={styles.cluster}>
		{actions.map((action) => (
			<ConnectionsByLineRowActionButton
				key={action.id}
				action={action}
				item={item}
				onAction={onAction}
			/>
		))}
	</Box>
);
